/**
 * Pre-Submit Validation Composable
 *
 * Owns the merged BA check that runs at the step 1 → step 2 transition.
 * Orchestrates the single pre-submit BA call (real client + hotel + auxiliaries)
 * and commits the fresh session/activity/code to the store so submitBooking
 * can feed them into ConvertResponse + B/BQ/BC.
 */

import { ref, computed, type Ref, type ComputedRef } from 'vue';
import { useCheckoutStore } from '@/stores/checkout';
import { performPreSubmitCheck } from '@/services/api/davinci/preSubmitCheck';
import { buildAddressCorrectionAlertData } from '@/utils/checkout/addressValidation';
import type { AddressCorrectionAlertData } from '@/types/schemas/checkout/addressValidation';
import type { PreSubmitCheckResult } from '@/types/schemas/checkout/preSubmitCheck';
import type { PpsExtraSelection } from '@/services/api/davinci/helpers';
import type { PpsService } from '@/types/schemas/pps';
import { requiresRequestBookingSwitch } from '@/constants/davinci/returnCodes';
import { COUNTRY_CODE_SET } from '@/constants/checkout/countries';
import type { CheckoutFormData } from '@/types/schemas/checkout';
import type { ContactFormExposed } from '@/types/checkout';
import { $t } from '@/composables/translations/useTranslate';
import type { DeepReadonly } from 'vue';

export interface UsePreSubmitValidationReturn {
  isValidating: Ref<boolean>;
  validationError: Ref<string | null>;
  validationResult: Ref<PreSubmitCheckResult | null>;

  hasAddressChanges: ComputedRef<boolean>;
  shouldStayOnForm: ComputedRef<boolean>;
  auxUnavailable: ComputedRef<boolean>;
  correctedAddress: ComputedRef<PreSubmitCheckResult['correctedAddress']>;
  addressCorrectionAlertData: ComputedRef<AddressCorrectionAlertData | null>;
  auxErrorMessage: ComputedRef<string | null>;

  validate: (formData: CheckoutFormData | DeepReadonly<CheckoutFormData>) => Promise<boolean>;
  applyAddressCorrections: (contactFormRef: ContactFormExposed | null) => void;
  clearValidation: () => void;
}

export function usePreSubmitValidation(): UsePreSubmitValidationReturn {
  const checkoutStore = useCheckoutStore();

  const isValidating = ref(false);
  const validationError = ref<string | null>(null);
  const validationResult = ref<PreSubmitCheckResult | null>(null);

  // Generation counter guards against stale results when validate() fires twice
  // in quick succession (e.g. double-click). The HTTP layer does not support
  // cancellation, so we race-protect at commit time instead.
  let generation = 0;

  const hasAddressChanges = computed(() => validationResult.value?.hasAddressChanges ?? false);
  const shouldStayOnForm = computed(() => validationResult.value?.shouldStayOnForm ?? false);
  const auxUnavailable = computed(() => validationResult.value?.auxUnavailable ?? false);
  const correctedAddress = computed(() => validationResult.value?.correctedAddress);

  const addressCorrectionAlertData = computed((): AddressCorrectionAlertData | null => {
    const result = validationResult.value;
    if (!result?.shouldStayOnForm || !result.hasAddressChanges) {
      return null;
    }

    return buildAddressCorrectionAlertData({
      addressChanges: result.addressChanges,
      shouldSwitchToRequestBooking: requiresRequestBookingSwitch(result.code),
    });
  });

  const auxErrorMessage = computed((): string | null => {
    if (!validationResult.value?.auxUnavailable) return null;
    return $t(
      'ibe.checkout.aux.unavailable',
      'One or more selected extras or insurance options are currently unavailable. Please adjust your selection and try again.'
    );
  });

  async function validate(
    formData: CheckoutFormData | DeepReadonly<CheckoutFormData>
  ): Promise<boolean> {
    const thisGeneration = ++generation;

    isValidating.value = true;
    validationError.value = null;
    validationResult.value = null;

    try {
      const roomCombination = checkoutStore.roomCombination;
      const productCode = checkoutStore.productCode;
      const travelers = checkoutStore.travelersSummary;

      if (!roomCombination || !productCode) {
        throw new Error('Missing room combination or product code');
      }

      if (checkoutStore.selectedInsuranceId !== null && checkoutStore.selectedInsurance === null) {
        console.warn(
          '[usePreSubmitValidation] selectedInsuranceId is set but no matching insurance found in sscInsurances'
        );
        validationError.value = $t(
          'ibe.checkout.validation.error',
          'An error occurred during validation. Please try again.'
        );
        return false;
      }

      const selectedExtras: PpsExtraSelection[] = [];
      for (const [bookingCodeId, serviceTypeCode] of checkoutStore.selectedExtrasMap.entries()) {
        const service = checkoutStore.ppsExtras.find((e) => e.BookingCodeId === bookingCodeId);
        if (!service) {
          console.warn(
            `[usePreSubmitValidation] Dropping stale selected extra: BookingCodeId ${bookingCodeId} not found in ppsExtras`
          );
          continue;
        }
        // Store exposes ppsExtras as readonly — helpers consume the plain shape; cast is safe.
        selectedExtras.push({ service: service as PpsService, serviceTypeCode });
      }

      const result = await performPreSubmitCheck({
        formData,
        travelers,
        roomCombination,
        productCode,
        selectedInsurance: checkoutStore.selectedInsurance,
        selectedExtras,
        travelType: checkoutStore.travelType ?? undefined,
        agencyNumber: checkoutStore.agencyNumber ?? undefined,
      });

      // A newer validate() call started while we were awaiting — drop this result
      if (thisGeneration !== generation) {
        return false;
      }

      validationResult.value = result;

      if (result.shouldStayOnForm) {
        return false;
      }

      // Defensive: only commit a fresh session when we're actually proceeding.
      if (result.baSession) {
        checkoutStore.applyPreSubmitResult({
          code: result.code,
          sessionId: result.baSession.sessionId,
          activityId: result.baSession.activityId,
          bookingResponse: result.baSession.bookingResponse,
        });
      }

      if (result.isValid) {
        return true;
      }

      validationError.value = $t(
        'ibe.checkout.validation.failed',
        'Validation failed. Please try again.'
      );
      return false;
    } catch (error) {
      if (thisGeneration !== generation) {
        return false;
      }
      console.error('[usePreSubmitValidation] Validation error:', error);
      validationError.value = $t(
        'ibe.checkout.validation.error',
        'An error occurred during validation. Please try again.'
      );
      return false;
    } finally {
      if (thisGeneration === generation) {
        isValidating.value = false;
      }
    }
  }

  function applyAddressCorrections(contactFormRef: ContactFormExposed | null): void {
    if (!contactFormRef || !correctedAddress.value) return;

    const correctedCountry = correctedAddress.value.country?.toLowerCase();
    const country =
      correctedCountry && COUNTRY_CODE_SET.has(correctedCountry) ? correctedCountry : undefined;

    const fieldMappings: Array<{ formField: string; value: string | undefined }> = [
      { formField: 'street', value: correctedAddress.value.street },
      { formField: 'postalCode', value: correctedAddress.value.postalCode },
      { formField: 'location', value: correctedAddress.value.city },
      { formField: 'country', value: country },
    ];

    for (const mapping of fieldMappings) {
      if (mapping.value) {
        contactFormRef.setFieldValue(mapping.formField, mapping.value);
      }
    }
  }

  function clearValidation(): void {
    generation++;
    validationResult.value = null;
    validationError.value = null;
  }

  return {
    isValidating,
    validationError,
    validationResult,
    hasAddressChanges,
    shouldStayOnForm,
    auxUnavailable,
    correctedAddress,
    addressCorrectionAlertData,
    auxErrorMessage,
    validate,
    applyAddressCorrections,
    clearValidation,
  };
}
